import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Checkout.css';

function Checkout() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        quantity: 1
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const ticketPrice = 19.00;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        }

        if (formData.quantity < 1 || formData.quantity > 10) {
            newErrors.quantity = 'Quantity must be between 1 and 10';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        // Simulate processing time
        setTimeout(() => {
            // Store order data for confirmation page
            const orderData = {
                ...formData,
                orderId: `ROTB-${Date.now()}`,
                eventName: 'Phase 6 Inception',
                eventDate: 'December 15, 2025',
                eventTime: '22:00 - 06:00',
                eventLocation: 'Underground Warehouse District',
                totalAmount: (ticketPrice * formData.quantity).toFixed(2),
                ticketPrice: ticketPrice.toFixed(2),
                orderDate: new Date().toLocaleDateString()
            };

            // Store in localStorage for the confirmation page
            localStorage.setItem('orderData', JSON.stringify(orderData));

            // Navigate to confirmation
            navigate('/order-confirmation');
        }, 2000);
    };

    const totalAmount = (ticketPrice * formData.quantity).toFixed(2);

    return (
        <div className="checkout-page">
            {/* Video Background */}
            <div className="checkout-video-container">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="checkout-background-video"
                >
                    <source src="/rotb_big.mp4" type="video/mp4" />
                </video>
                <div className="checkout-video-overlay"></div>
            </div>

            <div className="checkout-container">
                <div className="checkout-content">
                    <h1 className="checkout-title">CHECKOUT</h1>

                    <div className="checkout-layout">
                        {/* Order Summary */}
                        <div className="order-summary">
                            <h2 className="summary-title">ORDER SUMMARY</h2>
                            <div className="summary-item">
                                <span className="item-name">Phase 6 Inception</span>
                                <span className="item-details">December 15, 2025</span>
                            </div>
                            <div className="summary-pricing">
                                <div className="pricing-row">
                                    <span>Ticket Price:</span>
                                    <span>${ticketPrice.toFixed(2)}</span>
                                </div>
                                <div className="pricing-row">
                                    <span>Quantity:</span>
                                    <span>{formData.quantity}</span>
                                </div>
                                <div className="pricing-row total">
                                    <span>Total:</span>
                                    <span>${totalAmount}</span>
                                </div>
                            </div>
                        </div>

                        {/* Checkout Form */}
                        <form className="checkout-form" onSubmit={handleSubmit}>
                            <h2 className="form-title">TICKET HOLDER INFORMATION</h2>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">FIRST NAME *</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        className={`form-input ${errors.firstName ? 'error' : ''}`}
                                        placeholder="Enter first name"
                                    />
                                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">LAST NAME *</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        className={`form-input ${errors.lastName ? 'error' : ''}`}
                                        placeholder="Enter last name"
                                    />
                                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">EMAIL *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.email ? 'error' : ''}`}
                                    placeholder="Enter email address"
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">PHONE NUMBER *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.phone ? 'error' : ''}`}
                                    placeholder="Enter phone number"
                                />
                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">QUANTITY *</label>
                                <select
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleInputChange}
                                    className={`form-input ${errors.quantity ? 'error' : ''}`}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                                {errors.quantity && <span className="error-message">{errors.quantity}</span>}
                            </div>

                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'PROCESSING...' : `COMPLETE ORDER - $${totalAmount}`}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="checkout-navigation">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    ← BACK TO TICKETS
                </button>
                <button className="home-btn" onClick={() => navigate('/')}>
                    HOME
                </button>
            </div>
        </div>
    );
}

export default Checkout;
